/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/QaChecklistRail.tsx
 * FILE_ID: JOBHUB-QA-BUILD-RAIL-001
 * PURPOSE: Column 2 — the checklists inside the selected location.
 */
import { Plus } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QaChecklistGroup } from '../../../qa/qaApiHelpers'
import { InlineRenameInput } from '../../../qa/InlineRenameInput'
import { QaDragHandle } from '../../../qa/QaDragHandle'
import { useEffect, useRef, useState } from 'react'

export function QaChecklistRail({
  groups,
  selectedId,
  onSelect,
  onRename,
  onAdd,
  adding = false,
  disabled = false,
  selectedIds,
  onToggleSelected,
  startRenameId,
  onRenameStarted,
  onOpenMenu,
  reorderFor: _reorderFor,
  areaId,
  collapsed = false,
  onToggleCollapsed,
}: {
  groups: QaChecklistGroup[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onRename?: (checklistId: string, title: string) => void
  onAdd?: () => void
  adding?: boolean
  disabled?: boolean
  selectedIds?: ReadonlySet<string>
  onToggleSelected?: (checklistId: string) => void
  startRenameId?: string | null
  onRenameStarted?: () => void
  onOpenMenu?: (checklistId: string, event: { clientX: number; clientY: number }) => void
  reorderFor?: (checklistId: string) => {
    canMoveUp: boolean
    canMoveDown: boolean
    busy?: boolean
    onMoveUp: () => void
    onMoveDown: () => void
  } | undefined
  areaId?: string
  /** Set only at lg and up. Below lg the chip strip stays. */
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const hold = useRef<number | null>(null)
  const suppressUntil = useRef(0)
  function armMenu(checklistId: string, point: { clientX: number; clientY: number }) {
    if (!onOpenMenu) return
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = window.setTimeout(() => {
      suppressUntil.current = Date.now() + 750
      onOpenMenu(checklistId, point)
    }, 500)
  }
  function disarmMenu() {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }
  useEffect(() => {
    if (!startRenameId) return
    setEditingId(startRenameId)
    onRenameStarted?.()
  }, [startRenameId, onRenameStarted])

  const toggleTitle = collapsed ? 'Expand checklists' : 'Collapse checklists'
  const toggle = (
    <button
      type="button"
      className="shrink-0 px-0.5 text-xs font-semibold leading-none text-muted-foreground hover:text-foreground"
      aria-expanded={!collapsed}
      aria-label={toggleTitle}
      title={toggleTitle}
      data-testid="qa-toggle-checklists"
      onClick={() => onToggleCollapsed?.()}
    >
      {collapsed ? '>' : 'v'}
    </button>
  )
  if (collapsed) {
    return (
      <div className="flex min-w-0 items-center gap-1 px-1">
        <p className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Checklists
        </p>
        {toggle}
      </div>
    )
  }

  return (
    <div className="min-w-0 space-y-1">
      <div className="hidden items-center gap-1 px-1 lg:flex">
        <p className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Checklists
        </p>
        {toggle}
      </div>
      <div className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-x-visible lg:max-h-[var(--qa-pane-max-h,min(70vh,40rem))] lg:overflow-y-auto">
        {groups.map((group) => {
          const active = selectedId === group.checklist.id
          return (
            <div
              key={group.checklist.id}
              data-qa-rail-checklist={group.checklist.id}
              data-qa-checklist-row={group.checklist.id}
              data-qa-checklist-name={group.name}
              data-qa-checklist-area={areaId}
              onContextMenu={onOpenMenu ? (event) => {
                event.preventDefault()
                onOpenMenu(group.checklist.id, event)
              } : undefined}
              onTouchStart={onOpenMenu ? (event) => {
                const touch = event.touches[0]
                if (touch) armMenu(group.checklist.id, touch)
              } : undefined}
              onTouchEnd={disarmMenu}
              onTouchMove={disarmMenu}
              onClickCapture={(event) => {
                if (Date.now() > suppressUntil.current) return
                event.preventDefault()
                event.stopPropagation()
                suppressUntil.current = 0
              }}
              data-qa-drop={`checklist:${group.checklist.id}`}
              className={cn(
                'flex min-w-0 shrink-0 items-center gap-1 rounded-md px-1 py-0.5 data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary',
                active ? 'bg-primary/10' : 'hover:bg-muted',
              )}
            >
              {onToggleSelected && (
                <input
                  type="checkbox"
                  checked={selectedIds?.has(group.checklist.id) ?? false}
                  onChange={() => onToggleSelected(group.checklist.id)}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={`Select ${group.name}`}
                  className="h-3.5 w-3.5 shrink-0 rounded border-border"
                />
              )}
              <QaDragHandle kind="checklist" id={group.checklist.id} label={group.name} />
              {editingId === group.checklist.id && onRename ? (
                <InlineRenameInput
                  initialValue={group.name}
                  ariaLabel="Rename checklist"
                  className="min-w-[8rem] flex-1"
                  onSubmit={(title) => {
                    onRename(group.checklist.id, title)
                    setEditingId(null)
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <button
                  type="button"
                  title={active ? 'Click again to rename' : undefined}
                  onClick={() => {
                    if (active && onRename) setEditingId(group.checklist.id)
                    else onSelect(group.checklist.id)
                  }}
                  onDoubleClick={() => onRename && setEditingId(group.checklist.id)}
                  className={cn(
                    'min-w-0 flex-1 px-1 py-1 text-left text-xs',
                    active ? 'font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <span className="block min-w-0 truncate">
                    {group.checklist.display_number && (
                      <span className="mr-1 tabular-nums text-muted-foreground">
                        {group.checklist.display_number}
                      </span>
                    )}
                    {group.name}
                    <span className="ml-1 tabular-nums text-[10px]">
                      {group.done}/{group.total}
                    </span>
                  </span>
                  {(group.checklist.product_names?.length ?? 0) > 0 ? (
                    <span
                      className="block truncate text-[11px] text-muted-foreground"
                      title={group.checklist.product_names?.join(', ')}
                    >
                      {group.checklist.product_names?.join(', ')}
                    </span>
                  ) : null}
                </button>
              )}
            </div>
          )
        })}
      </div>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled || adding}
          className="mt-1 flex w-full items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add checklist
        </button>
      )}
    </div>
  )
}
